import { ResponseDeduplicator } from "../../services/responseDeduplicator";
import type { Middleware } from "@reduxjs/toolkit";
import { socketService } from "../../services/socket";
import type { RootState } from "../types";
import * as websocketActions from "../slices/websocketSlice";
import * as messagesActions from "../slices/messagesSlice";
import * as sessionsActions from "../slices/sessionsSlice";
import * as diagnosisActions from "../slices/diagnosisSlice";
import * as speechActions from "../slices/speechSlice";
import {
  exchangeTimingService,
  type BackendLLMTimings,
  type ExchangeTimingMetadata,
} from "../../services/exchangeTiming";

interface WebSocketAction {
  type: string;
  payload?: any;
}

interface PatientResponsePayload {
  response: string;
  scoringUpdate?: any;
  exchangeId?: string;
  timings?: BackendLLMTimings;
}

interface PatientFillerPayload {
  filler: string;
  originalQuestion: string;
  exchangeId?: string;
}

let currentSessionId: string | null = null;
let reconnectAttempts = 0;
let isDisconnecting = false; // Prevent infinite disconnect loops
let isConnecting = false; // Prevent multiple simultaneous connections
const maxReconnectAttempts = 3;
const responseDeduplicator = new ResponseDeduplicator();

export const websocketMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action) => {
    const { type, payload } = action as WebSocketAction;

    // Always call next first to update state
    const result = next(action);

    switch (type) {
      case "websocket/connect": {
        const { sessionId } = payload;

        console.log("🔌 WebSocket connect request:", {
          newSessionId: sessionId,
          currentSessionId,
          isConnecting,
          alreadyConnected: store.getState().websocket.isConnected,
        });

        // Prevent multiple simultaneous connections
        if (isConnecting) {
          console.log("🔌 Connection already in progress, ignoring");
          return result;
        }

        // If already connected to the same session, don't reconnect
        if (
          currentSessionId === sessionId &&
          store.getState().websocket.isConnected
        ) {
          console.log("🔌 Already connected to this session, skipping");
          return result;
        }

        isConnecting = true;

        // Disconnect existing connection if different session
        if (currentSessionId && currentSessionId !== sessionId) {
          console.log("🔌 Disconnecting from old session:", currentSessionId);
          try {
            socketService.disconnect();
            store.dispatch(websocketActions.disconnect());
          } catch (error) {
            console.warn("Error disconnecting old session:", error);
          }
        }

        responseDeduplicator.setSession(sessionId);
        currentSessionId = sessionId;
        store.dispatch(websocketActions.connectStart());

        try {
          const socket = socketService.connect(sessionId);

          // Set up event handlers
          socket.on("connect", () => {
            console.log("✅ WebSocket connected to session:", sessionId);
            reconnectAttempts = 0;
            isConnecting = false;
            store.dispatch(
              websocketActions.connectSuccess({
                connectionId: socket.id || "unknown",
              }),
            );
          });

          socket.on("disconnect", (reason) => {
            console.log("🔌 WebSocket disconnected:", reason);
            isConnecting = false;
            store.dispatch(websocketActions.disconnect());

            // Auto-reconnect on unexpected disconnects (but not manual ones)
            if (
              reason !== "io client disconnect" &&
              reconnectAttempts < maxReconnectAttempts &&
              !isDisconnecting
            ) {
              reconnectAttempts++;
              console.log(
                `🔄 Attempting reconnect ${reconnectAttempts}/${maxReconnectAttempts}`,
              );
              setTimeout(() => {
                if (currentSessionId && !isDisconnecting) {
                  store.dispatch({
                    type: "websocket/connect",
                    payload: { sessionId: currentSessionId },
                  });
                }
              }, 2000 * reconnectAttempts);
            }
          });

          socket.on("connect_error", (error) => {
            console.error("❌ WebSocket connection error:", error);
            isConnecting = false;
            store.dispatch(websocketActions.connectFailure(error.message));
          });

          // Handle patient responses with interruption awareness
          socket.on("patient-response", (data: PatientResponsePayload) => {
            console.log(
              "💬 Received patient response for session:",
              sessionId,
              data.response,
            );

            // Only process if this is for the current session
            const currentState = store.getState();
            if (currentState.sessions.current?.sessionId !== sessionId) {
              console.log("⚠️ Ignoring response for old session");
              return;
            }

            if (!responseDeduplicator.accept(sessionId, data.exchangeId)) return;

            // If we were interrupted, this must be the combined response - reset interruption state
            if (currentState.messages.isInterrupted) {
              console.log(
                "🎤 Received combined response after interruption, resetting interruption state",
              );
              store.dispatch(messagesActions.setInterrupted(false));
              // Don't return - process this response normally
            }

            // Remove thinking message
            store.dispatch(messagesActions.removeThinkingMessage());

            if (data.response) {
              const exchangeId =
                exchangeTimingService.markPatientResponseReceived(
                  data.exchangeId,
                  data.response,
                  data.timings,
                );

              // Add patient response
              store.dispatch(
                messagesActions.addMessage({
                  type: "patient",
                  content: data.response,
                }),
              );

              // Update processing state
              store.dispatch(messagesActions.setProcessing(false));

              // Increment questions asked
              store.dispatch(sessionsActions.incrementQuestionsAsked());

              // Dispatch messageReceived action for other middleware (especially avatar)
              store.dispatch(
                websocketActions.messageReceived({
                  type: "patient-response",
                  data: data,
                }),
              );

              exchangeTimingService.completeIfAvatarWasNotConnected(exchangeId);

              // Also emit custom event for avatar (backup method)
              window.dispatchEvent(
                new CustomEvent("patient-response", {
                  detail: { response: data.response },
                }),
              );
            } else {
              exchangeTimingService.completeWithoutAvatar(
                data.exchangeId,
                "no_patient_response",
              );
            }

            // Handle scoring update if present
            if (data.scoringUpdate && data.scoringUpdate.newPointsAwarded > 0) {
              store.dispatch(
                diagnosisActions.updateScoring(
                  data.scoringUpdate.updatedScoringLogic,
                ),
              );
            }
          });

          // Handle patient fillers with interruption awareness
          socket.on("patient-filler", (data: PatientFillerPayload) => {
            console.log(
              "💭 Received patient filler for session:",
              sessionId,
              data.filler,
            );

            // Only process if this is for the current session
            const currentState = store.getState();
            if (currentState.sessions.current?.sessionId !== sessionId) {
              console.log("⚠️ Ignoring filler for old session");
              return;
            }

            // Check if session is currently interrupted - still allow fillers
            if (currentState.messages.isInterrupted) {
              console.log(
                "🎤 Allowing filler during interruption (immediate response)",
              );
            }

            exchangeTimingService.markFillerReceived(
              data.exchangeId,
              data.filler,
            );

            // Dispatch messageReceived action for other middleware
            store.dispatch(
              websocketActions.messageReceived({
                type: "patient-filler",
                data: data,
              }),
            );

            // Emit custom event for avatar immediately
            window.dispatchEvent(
              new CustomEvent("patient-filler", {
                detail: {
                  filler: data.filler,
                  originalQuestion: data.originalQuestion,
                },
              }),
            );
          });

          // Handle interruption acknowledgment
          socket.on(
            "interruption-acknowledged",
            (data: { message: string }) => {
              console.log("🎤 Interruption acknowledged:", data.message);
              const currentState = store.getState();

              // Set interruption state
              store.dispatch(messagesActions.setInterrupted(true));
              store.dispatch(
                messagesActions.setInterruptionMessage(data.message),
              );

              // Update the thinking message to show interruption
              if (currentState.messages.lastThinkingMessageId) {
                store.dispatch(
                  messagesActions.updateMessage({
                    id: currentState.messages.lastThinkingMessageId,
                    content: data.message,
                  }),
                );
              } else {
                // Add interruption message if no thinking message exists
                store.dispatch(messagesActions.addSystemMessage(data.message));
              }
            },
          );

          // Handle interruption timeout
          socket.on(
            "interruption-timeout",
            (data: { combinedQuestion: string }) => {
              console.log("🎤 Interruption timeout:", data.combinedQuestion);

              // End interruption collection
              store.dispatch(speechActions.endInterruption());

              // Reset interruption state
              store.dispatch(messagesActions.setInterrupted(false));

              // Add system message about timeout
              store.dispatch(
                messagesActions.addSystemMessage(
                  `Interruption timeout reached. Processing combined question: "${data.combinedQuestion}"`,
                ),
              );
            },
          );

          socket.on(
            "combined-question",
            (data: { combinedQuestion: string }) => {
              console.log(
                "🎤 Updating question display to:",
                data.combinedQuestion,
              );

              const currentState = store.getState();
              const messages = currentState.messages.items;

              // Find and remove the last medical provider message
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].type === "medical-provider") {
                  store.dispatch(messagesActions.removeMessage(messages[i].id));
                  break;
                }
              }

              // Add the combined question as a new medical provider message
              store.dispatch(
                messagesActions.addMessage({
                  type: "medical-provider",
                  content: data.combinedQuestion,
                }),
              );
            },
          );

          // Handle system messages
          socket.on("system-message", (data: { message: string }) => {
            console.log(
              "📢 Received system message for session:",
              sessionId,
              data.message,
            );
            store.dispatch(messagesActions.addSystemMessage(data.message));
          });

          // Handle errors
          socket.on("error", (error: { message: string; code?: string }) => {
            console.error("❌ WebSocket error for session:", sessionId, error);
            exchangeTimingService.completeMostRecentWithoutAvatar(
              "websocket_error",
            );
            store.dispatch(messagesActions.removeThinkingMessage());
            store.dispatch(
              messagesActions.addSystemMessage(`Error: ${error.message}`),
            );
            store.dispatch(messagesActions.setProcessing(false));

            // Reset interruption state on error
            store.dispatch(messagesActions.setInterrupted(false));
            store.dispatch(speechActions.endInterruption());
          });

          // Handle diagnosis results
          socket.on(
            "diagnosis-result",
            (result: {
              evaluation: string;
              feedback: string;
              completionRate: number;
              correctDiagnosis: string;
            }) => {
              console.log(
                "🏥 Received diagnosis evaluation for session:",
                sessionId,
                {
                  evaluationLength: result.evaluation.length,
                  completionRate: result.completionRate,
                },
              );

              // Only process if this is for the current session
              const currentState = store.getState();
              if (currentState.sessions.current?.sessionId !== sessionId) {
                console.log("⚠️ Ignoring diagnosis result for old session");
                return;
              }

              // Remove processing state
              store.dispatch(messagesActions.setProcessing(false));

              // Add the LLM evaluation as a system message
              const evaluationMessage =
                `📋 Diagnosis Evaluation:\n\n${result.evaluation}\n\n` +
                `📊 Diagnostic Thoroughness: ${result.completionRate}%\n` +
                `🎯 Correct Diagnosis: ${result.correctDiagnosis}`;

              store.dispatch(
                messagesActions.addSystemMessage(evaluationMessage),
              );
            },
          );

          // Handle scoring evaluation started
          socket.on("scoring-evaluating", () => {
            console.log(
              "📊 Scoring evaluation started for session:",
              sessionId,
            );
          });

          // Handle scoring updates
          socket.on(
            "scoring-update",
            (data: {
              criteriaMetThisExchange: string[];
              newPointsAwarded: number;
              updatedScoringLogic: any[];
              evaluationNotes: string;
              crossCategoryDetections: any[];
            }) => {
              console.log(
                "🏆 Received scoring update for session:",
                sessionId,
                data,
              );

              // Only process if this is for the current session
              const currentState = store.getState();
              if (currentState.sessions.current?.sessionId !== sessionId) {
                console.log("⚠️ Ignoring scoring update for old session");
                return;
              }

              // Always update scoring state with latest from server
              store.dispatch(
                diagnosisActions.updateScoring(data.updatedScoringLogic),
              );

              // Show notification only when points are earned
              if (data.newPointsAwarded > 0) {
                store.dispatch(
                  messagesActions.addSystemMessage(
                    `+${data.newPointsAwarded} points awarded for: ${data.criteriaMetThisExchange.join(", ")}`,
                  ),
                );
              }
            },
          );
        } catch (error: any) {
          console.error("❌ Failed to connect WebSocket:", error);
          isConnecting = false;
          store.dispatch(websocketActions.connectFailure(error.message));
        }

        break;
      }

      case "websocket/disconnect": {
        // Prevent infinite disconnect loops
        if (isDisconnecting) {
          console.log("🔌 Already disconnecting, ignoring duplicate request");
          return result;
        }

        console.log("🔌 WebSocket disconnect request");
        isDisconnecting = true;

        try {
          socketService.disconnect();
          currentSessionId = null;
          reconnectAttempts = 0;
          isConnecting = false;

          // Reset interruption state on disconnect
          store.dispatch(messagesActions.setInterrupted(false));
          store.dispatch(speechActions.endInterruption());

          // Don't dispatch websocketActions.disconnect() here as it's already handled by next(action)
          console.log("🔌 WebSocket disconnected successfully");
        } catch (error) {
          console.error("Error during WebSocket disconnect:", error);
        } finally {
          isDisconnecting = false;
        }

        break;
      }

      case "websocket/sendMessage": {
        const socket = socketService.getSocket();
        if (socket && socket.connected) {
          const { event, data } = payload;
          console.log("📤 Sending WebSocket message:", {
            event,
            sessionId: currentSessionId,
          });
          socket.emit(event, data);

          // Update ping time
          store.dispatch(websocketActions.updatePing());
        } else {
          console.error("❌ Cannot send message: WebSocket not connected");
        }
        break;
      }

      case "websocket/askQuestion": {
        const socket = socketService.getSocket();
        if (socket && socket.connected) {
          const { question, caseId, sessionId, timing } = payload as {
            question: string;
            caseId: string;
            sessionId: string;
            timing?: ExchangeTimingMetadata;
          };

          console.log("❓ Sending question via WebSocket:", {
            question,
            caseId,
            sessionId,
            currentSessionId,
          });

          // Verify we're sending to the correct session
          if (sessionId !== currentSessionId) {
            console.error(
              "❌ Session mismatch! Trying to send to:",
              sessionId,
              "but connected to:",
              currentSessionId,
            );
            store.dispatch(
              messagesActions.addSystemMessage(
                "Error: Session mismatch. Please refresh the page.",
              ),
            );
            return result;
          }

          // Reset interruption state when asking new question
          store.dispatch(messagesActions.setInterrupted(false));

          // Add medical provider message
          store.dispatch(
            messagesActions.addMessage({
              type: "medical-provider",
              content: question,
            }),
          );

          // Add thinking message
          store.dispatch(
            messagesActions.addMessage({
              type: "ai-thinking",
              content: "Patient is thinking...",
            }),
          );

          // Set processing state
          store.dispatch(messagesActions.setProcessing(true));

          const stateAfterMessageUpdate = store.getState();
          const fallbackInputAt = performance.now();
          const exchangeId = exchangeTimingService.startExchange({
            inputMode: timing?.inputMode || "typed",
            inputStartedAt: timing?.inputStartedAt ?? fallbackInputAt,
            inputCompletedAt: timing?.inputCompletedAt ?? fallbackInputAt,
            transcriptionStartedAt: timing?.transcriptionStartedAt,
            transcriptionCompletedAt: timing?.transcriptionCompletedAt,
            sessionId,
            caseId,
            fieldId: stateAfterMessageUpdate.sessions.current?.fieldId,
            question,
            avatarEnabled: stateAfterMessageUpdate.avatar.isEnabled,
            avatarConnected: stateAfterMessageUpdate.avatar.isConnected,
          });

          // Send question via WebSocket
          socket.emit("ask-question", {
            question,
            caseId,
            sessionId,
            exchangeId,
            inputMode: timing?.inputMode || "typed",
          });
        } else {
          console.error("❌ Cannot ask question: WebSocket not connected");
          store.dispatch(
            messagesActions.addSystemMessage(
              "Error: Not connected to server. Please refresh the page.",
            ),
          );
        }
        break;
      }

      case "websocket/submitDiagnosis": {
        const socket = socketService.getSocket();
        if (socket && socket.connected) {
          const { diagnosis, caseId, sessionId } = payload;
          console.log("🏥 Submitting diagnosis via WebSocket:", {
            diagnosis,
            sessionId,
            currentSessionId,
          });

          // Verify session
          if (sessionId !== currentSessionId) {
            console.error("❌ Session mismatch for diagnosis!");
            store.dispatch(
              messagesActions.addSystemMessage(
                "Error: Session mismatch. Please refresh the page.",
              ),
            );
            return result;
          }

          socket.emit("submit-diagnosis", { diagnosis, caseId, sessionId });
        } else {
          console.error("❌ Cannot submit diagnosis: WebSocket not connected");
          store.dispatch(
            messagesActions.addSystemMessage(
              "Error: Not connected to server. Please refresh the page.",
            ),
          );
        }
        break;
      }
    }

    return result;
  };
